<template>
  <div class="d-flex filter-wrapper filter-inner-wrapper flex-row toggle-click-parent">
    <div class="toggle-click-child" v-if="slots.search">
      <slot name="search"></slot>
    </div>

    <div class="toggle-click-child">
      <LiveButton :isActive="isLiveActive" @click="setActive(DateTimeFilterTypes.Live)" />
    </div>
    <div class="toggle-click-child">
      <Last24Hours :isActive="isLast24Active" @click="setActive(DateTimeFilterTypes.Last24)" />
    </div>
    <div class="toggle-click-child">
      <DatePicker
        :isActive="isDatePickerActive"
        @onOpen="setActive(DateTimeFilterTypes.DatePicker)"
      />
    </div>
    <div class="toggle-click-child" @click="() => clickPlayLive()">
      <PlayButton :is-live-active="isLiveActive" :is-playing-live="isPlayingLive"></PlayButton>
    </div>
    <div class="flex-fill toggle-click-parent max-width">
      <Slider :isLoading="isLoading" :state="activeButton" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DatePicker from './DatePicker.vue'
import LiveButton from './LiveButton.vue'
import Last24Hours from './LastXHoursButton.vue'
import Slider from './TimeRange.vue'
import { ref, defineProps, computed, defineEmits, onMounted, watch } from 'vue'
import { useDateTimeStore } from '@/stores/DateTimeFilterStore'
import { DateTimeFilterTypes } from '@/utilities/enums'
import PlayButton from './PlayButton.vue'
import { useSlots } from 'vue'

const slots = useSlots()

const activeButton = ref(DateTimeFilterTypes.Live)

const isLiveActive = computed(() => activeButton.value === DateTimeFilterTypes.Live)
const isLast24Active = computed(() => activeButton.value === DateTimeFilterTypes.Last24)
const isDatePickerActive = computed(() => activeButton.value === DateTimeFilterTypes.DatePicker)

const emit = defineEmits(['filterValueChanged'])

const dateTimeStore = useDateTimeStore()
const isPlayingLive = ref(false)

let timerId: any | null = null

let previousCounter = ref(dateTimeStore.updateCounter)

defineProps({
  isLoading: Boolean
})

function clickPlayLive() {
  if (isPlayingLive.value) {
    stopActiveTimer()
  } else {
    startActiveTimer()
  }
}

function checkUpdateCounter() {
  if (dateTimeStore.updateCounter !== previousCounter.value) {
    previousCounter.value = dateTimeStore.updateCounter
    clearTimeout(timerId)
    dateTimeStore.incrementTime()
  }
  // for optimal playback speed
  timerId = setTimeout(checkUpdateCounter, 4000)
}

function watchUpdateCounter() {
  checkUpdateCounter()
}

function startActiveTimer() {
  isPlayingLive.value = true
  previousCounter.value = dateTimeStore.updateCounter

  dateTimeStore.incrementTime()

  watchUpdateCounter()
}

function stopActiveTimer() {
  isPlayingLive.value = false
  if (timerId) {
    clearTimeout(timerId)
    timerId = null
  }
}

const setActive = (type: DateTimeFilterTypes) => {
  activeButton.value = type

  if (type === DateTimeFilterTypes.Live) {
    stopActiveTimer()
    dateTimeStore.setIsLive(true)
    dateTimeStore.setDateTime(new Date())
    emit('filterValueChanged', { filterTimestamp: null })
  } else {
    dateTimeStore.setIsLive(false)
  }
}

watch(
  () => dateTimeStore.resetCounter,
  () => {
    setActive(DateTimeFilterTypes.Live)
  }
)

watch(
  () => dateTimeStore.haltPlayButton,
  (newVal) => {
    if (newVal) {
      stopActiveTimer()
      setActive(DateTimeFilterTypes.Live)
      dateTimeStore.updateHalting(false)
    }
  }
)

onMounted(() => {
  setActive(DateTimeFilterTypes.Live)
})
</script>

<style scoped>
/* Component-specific styles go here */

.max-width {
  max-width: 1076px;
}

.filter-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
}

.filter-inner-wrapper {
  display: flex;
  height: var(--size-H1, 32px);
  align-items: center;
  gap: 7px;
  align-self: stretch;
}

.toggle-click-parent {
  pointer-events: none;
}

.toggle-click-child {
  pointer-events: auto;
  cursor: pointer;
}
</style>
